import DeleteOutlinedIcon from '@offline-mui/icons-material/DeleteOutlined'
import DownloadIcon from '@offline-mui/icons-material/Download'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@offline-mui/material'
import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { accountingRequest, accountingUrl, formatMoney, jsonInit, when } from '../api'
import { DownloadLabel, downloadFrom } from '../download'
import { LeoHeader } from '../LeoHeader'
import { useLoggedInName } from '../loggedInUser'

// SND-14277 / SND-14308: BSP close (suppliers that close weekly) and Monthly close (suppliers that close monthly).
// Run records an open run for an office, a supplier and dates (its report is downloaded from the runs table); Close
// closes those lines with a dummy check if they still add up to what the run found. Lines are always read from the
// line items, and a closed run keeps the lines it closed. Delete removes an open run for good. The logged-in user is
// saved as who ran or closed it.

export type CloseKind = 'weekly' | 'monthly'

const RUN_PAGE = '/invoice/reports/bsp-runs'

const KIND_TEXT: Record<CloseKind, { title: string; close: string; empty: string }> = {
  weekly: {
    title: 'BSP close',
    close: 'Close BSP',
    empty: 'No BSP close runs yet.',
  },
  monthly: {
    title: 'Monthly close',
    close: 'Close month',
    empty: 'No monthly close runs yet.',
  },
}

type Action = 'run' | 'close' | 'delete'


function ymd(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

// Default dates: last week's Monday to Sunday for BSP close, last month for Monthly close, as YYYY-MM-DD.
function defaultDates(kind: CloseKind) {
  const today = new Date()
  if (kind === 'monthly') {
    return {
      from: ymd(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      to: ymd(new Date(today.getFullYear(), today.getMonth(), 0)),
    }
  }
  const monday = new Date(today)
  monday.setDate(today.getDate() - (((today.getDay() + 6) % 7) + 7))
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  return { from: ymd(monday), to: ymd(sunday) }
}

function headCell(label: string, right = false) {
  return (
    <TableCell key={label} align={right ? 'right' : 'left'} sx={{ color: 'common.white', fontWeight: 700, border: 0 }}>
      {label}
    </TableCell>
  )
}

function statusChip(status: string) {
  return <Chip size="small" color={status === 'closed' ? 'success' : 'warning'} label={status === 'closed' ? 'Closed' : 'Open'} />
}

function runLabel(run: any) {
  return `${run.office} ${run.supplier} ${run.from} to ${run.to}`
}

// Confirms Delete. Run and Close go ahead at once as the logged-in user; they open this only to ask the name when
// nobody is logged in.
function NameDialog({
  kind,
  action,
  run,
  busy,
  onCancel,
  onConfirm,
}: {
  kind: CloseKind
  action: Action | null
  run: any
  busy: boolean
  onCancel: () => void
  onConfirm: (name: string) => void
}) {
  const loggedIn = useLoggedInName()
  const [name, setName] = useState('')
  useEffect(() => {
    if (action) setName('')
  }, [action])
  if (!action) return null
  const text = KIND_TEXT[kind]
  const title = action === 'run' ? 'Run the report' : action === 'close' ? text.close : 'Delete this run permanently'
  const button = action === 'run' ? 'Run' : action === 'close' ? text.close : 'Delete run'
  // The logged-in user is saved as who acted; the name is asked only when nobody is logged in.
  const askName = action !== 'delete' && !loggedIn
  const valid = !askName || name.trim().length >= 2
  return (
    <Dialog open onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <Box
        component="form"
        onSubmit={(event: any) => {
          event.preventDefault()
          if (valid && !busy) onConfirm(action === 'delete' ? '' : loggedIn || name.trim())
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
        <DialogContent>
          {action === 'close' && run ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Closes the {run.rowCount} line{run.rowCount === 1 ? '' : 's'} of run {run.id} ({runLabel(run)})
              {kind === 'weekly' ? ' and fills in their BSP settlement' : ''} with a dummy check. It only goes ahead if the
              lines still add up to what the run found.
            </Alert>
          ) : null}
          {action === 'delete' && run ? (
            <Alert severity="warning">
              Permanently removes run {run.id} ({runLabel(run)}); it cannot be undone. The same dates can then be run again as
              a new run. Line items are not changed.
            </Alert>
          ) : null}
          {askName ? (
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Your name"
              value={name}
              onChange={(event) => setName(event.target.value.slice(0, 60))}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button type="submit" variant="contained" color={action === 'delete' ? 'error' : 'warning'} disabled={!valid || busy}>
            {busy ? 'Working…' : button}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

function downloadRunCsv(run: any) {
  return downloadFrom(
    `/bsp-runs/${run.id}/report.csv`,
    `${run.kind === 'monthly' ? 'Monthly' : 'BSP'}-${run.office}-${run.supplier}-${run.from}-to-${run.to}-run-${run.id}.csv`,
    'Could not download the report.',
  )
}

// Reports → BSP close / Monthly close: the latest 10 runs, then the fields to download a new report.
export function CloseRunsSection({ kind }: { kind: CloseKind }) {
  const text = KIND_TEXT[kind]
  const [offices, setOffices] = useState<any[]>([])
  const [suppliers, setSuppliers] = useState<any[]>([])
  const [office, setOffice] = useState('')
  const [supplier, setSupplier] = useState('')
  const [from, setFrom] = useState(defaultDates(kind).from)
  const [to, setTo] = useState(defaultDates(kind).to)
  const [runs, setRuns] = useState<any[]>([])
  const loggedIn = useLoggedInName()
  const [pending, setPending] = useState<{ action: Action; run?: any } | null>(null)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [mismatch, setMismatch] = useState<any>(null)
  const [notice, setNotice] = useState('')
  const [lastRunId, setLastRunId] = useState<number | null>(null)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  async function loadRuns() {
    const body = await accountingRequest(`/bsp-runs?kind=${kind}&limit=10`)
    setRuns(body.runs || [])
  }

  useEffect(() => {
    accountingRequest('/offices')
      .then((body) => setOffices(body.offices || []))
      .catch(() => setOffices([]))
    accountingRequest(`/bsp-runs/suppliers?kind=${kind}`)
      .then((body) => setSuppliers(body.suppliers || []))
      .catch(() => setSuppliers([]))
    loadRuns().catch((err) => setError(err.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind])

  // The API sends the close differences with the error; accountingRequest keeps only the message, so call fetch here.
  async function post(path: string, payload: any) {
    const response = await fetch(accountingUrl(path), jsonInit('POST', payload))
    const body = await response.json().catch(() => ({}))
    if (!response.ok) {
      const failure: any = new Error(body.message || 'Request failed')
      failure.details = body.details
      throw failure
    }
    return body
  }

  async function confirm(name: string, step = pending) {
    if (!step) return
    const { action, run } = step
    setBusy(action)
    setError('')
    setMismatch(null)
    setNotice('')
    try {
      let result: any
      if (action === 'run') {
        result = await post('/bsp-runs', { kind, office, supplier, from, to, runBy: name })
        setNotice(`Run ${result.id}: ${result.rowCount} line${result.rowCount === 1 ? '' : 's'} for ${runLabel(result)}. Download its report from the runs table.`)
      } else if (action === 'close') {
        result = await post(`/bsp-runs/${run.id}/close`, { closedBy: name })
        setNotice(`Run ${result.id} closed by ${result.closedBy}: ${result.rowCount} line items closed with dummy check ${result.dummyCheck}.`)
      } else {
        result = await post(`/bsp-runs/${run.id}/delete`, {})
        setNotice(`Run ${result.id} deleted permanently. Run ${result.office} ${result.supplier} ${result.from} to ${result.to} again to make a new run.`)
      }
      setLastRunId(action === 'delete' ? null : result.id)
      setPending(null)
      await loadRuns()
    } catch (err: any) {
      setPending(null)
      setError(err.message)
      if (err.details) setMismatch(err.details)
    } finally {
      setBusy('')
    }
  }

  async function download(run: any) {
    setError('')
    setDownloadingId(run.id)
    try {
      await downloadRunCsv(run)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setDownloadingId(null)
    }
  }

  const datesOk = Boolean(from) && Boolean(to) && from <= to

  return (
    <Box>
      <Typography variant="overline" color="info.main" sx={{ display: 'block', fontWeight: 700, mb: 1 }}>Recent runs (latest 10)</Typography>
      <TableContainer component={Card} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'info.main' }}>
              {[headCell('Run ID'), headCell('Run date/time'), headCell('Run by'), headCell('Office'), headCell('Supplier'), headCell('Dates'), headCell('Status'), headCell('Check number'), headCell('')]}
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run) => (
              <TableRow key={run.id} sx={{ bgcolor: run.id === lastRunId ? 'rgba(233, 180, 76, 0.18)' : undefined }}>
                <TableCell sx={{ fontWeight: 700 }}>{run.id}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(run.runAt)}</TableCell>
                <TableCell>{run.runBy}</TableCell>
                <TableCell>{run.office}</TableCell>
                <TableCell>{run.supplier ? `${run.supplier} ${run.supplierName}` : '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{run.from} to {run.to}</TableCell>
                <TableCell>{statusChip(run.status)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{run.status === 'closed' ? run.dummyCheck : ''}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {run.status === 'open' ? (
                    <>
                      <Link component="button" type="button" disabled={downloadingId === run.id} onClick={() => download(run)} sx={{ fontWeight: 600, mr: 2 }}>
                        <DownloadLabel busy={downloadingId === run.id}>Download Report</DownloadLabel>
                      </Link>
                      <Link component="button" type="button" disabled={Boolean(busy)} onClick={() => (loggedIn ? confirm(loggedIn, { action: 'close', run }) : setPending({ action: 'close', run }))} sx={{ fontWeight: 700, mr: 1 }}>{text.close}</Link>
                      <Tooltip title="Delete run">
                        <IconButton size="small" aria-label={`Delete run ${run.id}`} onClick={() => setPending({ action: 'delete', run })} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                          <DeleteOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Link href={`${RUN_PAGE}/${run.id}`} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>Summary</Link>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!runs.length ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary">{text.empty}</Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="overline" color="info.main" sx={{ display: 'block', fontWeight: 700, mb: 1 }}>New report</Typography>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 1.5 }}>
            <TextField select size="small" label="Office" value={office} onChange={(event) => setOffice(event.target.value)} sx={{ minWidth: 200 }}>
              {offices.map((row) => (
                <MenuItem key={row.code} value={row.code}>{row.name || row.code}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Supplier"
              value={supplier}
              onChange={(event) => setSupplier(event.target.value)}
              helperText={suppliers.length ? ' ' : `No supplier is set to close ${kind} (Suppliers → Close?)`}
              sx={{ minWidth: 220 }}
            >
              {suppliers.map((row) => (
                <MenuItem key={row.code} value={row.code}>{row.code} {row.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="date"
              label="Start date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              size="small"
              type="date"
              label="End date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              error={Boolean(from) && Boolean(to) && to < from}
              helperText={from && to && to < from ? 'End must be on or after the start' : ' '}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: from || undefined } }}
            />
            <Button
              variant="contained"
              color="warning"
              disabled={!office || !supplier || !datesOk || Boolean(busy)}
              sx={{ height: 40 }}
              onClick={() => (loggedIn ? confirm(loggedIn, { action: 'run' }) : setPending({ action: 'run' }))}
            >
              {busy === 'run' ? 'Running…' : 'Run'}
            </Button>
          </Stack>
          {busy ? <LinearProgress color="warning" sx={{ mt: 2 }} /> : null}
          {error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
              {mismatch?.now ? (
                <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                  <li>At run: {mismatch.downloaded.lines ?? 0} lines, payable {formatMoney(mismatch.downloaded.payable)}</li>
                  <li>Now: {mismatch.now.lines} lines, payable {formatMoney(mismatch.now.payable)}</li>
                </Box>
              ) : null}
            </Alert>
          ) : null}
          {notice ? <Alert severity="success" sx={{ mt: 2 }} onClose={() => setNotice('')}>{notice}</Alert> : null}
        </CardContent>
      </Card>

      <NameDialog
        kind={kind}
        action={pending?.action || null}
        run={pending?.run}
        busy={Boolean(busy)}
        onCancel={() => setPending(null)}
        onConfirm={confirm}
      />
    </Box>
  )
}

function amountCell(value: unknown, bold = false) {
  if (value == null || value === '') return ''
  const number = Number(value) || 0
  return (
    <Typography component="span" variant="body2" sx={{ fontWeight: bold ? 700 : 400, color: number < 0 ? 'error.main' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
      {formatMoney(number)}
    </Typography>
  )
}

function Figure({ label, children }: { label: string; children: any }) {
  return (
    <Card variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 150 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Box sx={{ fontSize: 18, fontWeight: 700 }}>{children}</Box>
    </Card>
  )
}

// /invoice/reports/bsp-runs/:runId (opens in a new tab): a run's overall figures, read from the line items (for a
// closed run, the lines it closed). The lines themselves are in the download: the line count or Download Report.
export function BspRunPage() {
  const { runId } = useParams<{ runId: string }>()
  const history = useHistory()
  const [run, setRun] = useState<any>(null)
  const [creatingTransfer, setCreatingTransfer] = useState(false)
  const loggedIn = useLoggedInName()
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    accountingRequest(`/bsp-runs/${encodeURIComponent(runId)}`)
      .then((body) => setRun(body))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [runId])

  async function download() {
    setError('')
    setDownloading(true)
    try {
      await downloadRunCsv(run)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setDownloading(false)
    }
  }

  // SND-14329: one transfer invoice per closed BSP close (agency C0099, PNR XXXXXX, supplier 001 for minus Our
  // Commission, RA debit with the dummy check). Once made, the button opens it instead.
  async function createTransferInvoice() {
    setError('')
    setCreatingTransfer(true)
    try {
      const invoice = await accountingRequest(
        `/bsp-runs/${encodeURIComponent(run.id)}/transfer-invoice`,
        jsonInit('POST', { createdBy: loggedIn || 'Control Center' }),
      )
      history.push(`/invoice/${encodeURIComponent(invoice.invoiceNumber)}`)
    } catch (err: any) {
      setError(err.message)
      setCreatingTransfer(false)
    }
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 2 }}>
      <LeoHeader sx={{ mb: 3 }} />
      {loading ? <LinearProgress color="warning" /> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {run ? (
        <>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {run.title} run {run.id} · {run.office} · {run.supplier} {run.supplierName} · {run.from} to {run.to}
            </Typography>
            {statusChip(run.status)}
            <Box sx={{ flex: 1 }} />
            {run.status === 'open' ? (
              <Button variant="contained" color="secondary" size="small" startIcon={<DownloadIcon />} disabled={downloading} onClick={download}>
                <DownloadLabel busy={downloading}>Download Report</DownloadLabel>
              </Button>
            ) : null}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Run {when(run.runAt)} by {run.runBy}
            {run.status === 'closed' ? ` · closed ${when(run.closedAt)} by ${run.closedBy} · dummy check ${run.dummyCheck}` : ''}
          </Typography>

          <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1.5, mb: 2, alignItems: 'stretch' }}>
              <Figure label={run.status === 'closed' ? 'Total line items affected' : 'Line items'}>
                <Link component="button" type="button" disabled={downloading} onClick={download} title="Download these line items" sx={{ fontWeight: 700, fontSize: 18 }}>
                  <DownloadLabel busy={downloading}>{run.totals.lines}</DownloadLabel>
                </Link>
              </Figure>
              <Figure label="Cost">{amountCell(run.totals.cost, true)}</Figure>
              <Figure label="Payable">{amountCell(run.totals.payable, true)}</Figure>
              <Figure label="Credit Card Sales">{amountCell(run.totals.creditCardSales, true)}</Figure>
              <Figure label="Cash Sales">{amountCell(run.totals.cashSales, true)}</Figure>
              <Figure label="Our Commission">{amountCell(run.totals.ourCommission, true)}</Figure>
              {run.status === 'closed' && run.kind === 'weekly' ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {run.transferInvoiceNumber ? (
                    <Button variant="contained" color="warning" onClick={() => history.push(`/invoice/${encodeURIComponent(run.transferInvoiceNumber)}`)}>
                      Open Transfer Invoice {run.transferInvoiceNumber}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="warning"
                      disabled={creatingTransfer || !(Number(run.totals.ourCommission) > 0)}
                      title={Number(run.totals.ourCommission) > 0 ? undefined : 'No our commission to transfer'}
                      onClick={createTransferInvoice}
                    >
                      {creatingTransfer ? 'Creating…' : 'Create Transfer Invoice'}
                    </Button>
                  )}
                </Box>
              ) : null}
          </Stack>

        </>
      ) : null}

    </Box>
  )
}
